import { CONTROL_TOOLTIPS } from '@src/containers/Blotters/tooltips';
import { useFeatureFlag } from '@src/hooks';
import { usePredictionsContext } from '@src/providers/PredictionsProvider';
import {
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CollapseAllButton,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  Toggle,
  columnTypes,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useSecuritiesContext,
  useWSFilterPipe,
  useWsBlotterTable,
  type Leaves,
  type Position,
} from '@talos/kyoko';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { defaultPredictionsBlotterColumns, defaultPredictionsBlotterFilter } from '../defaults';
import { getPositionsBlotterRowAsset, getPositionsBlotterRowKey } from '../positionBlotterRow';
import { GROUP_COL_WIDTH } from '../tokens';
import type { ClonePositionsTabFunc, PredictionsPositionsTabProps } from '../types';
import { usePositionsBaseRequest } from '../usePositionsBaseRequest';
import { usePositionsMenu } from '../usePositionsMenu';
import { useShowAdjustMarketAccountPositionButton } from '../useShowAdjustMarketAccountPositionButton';
import { isPredictionPositionExcludedByFilters, resolvePredictionSymbolMetadata } from './predictionSymbolMetadata';
import { usePositionsPredictionsBlotterGrouping } from './usePositionsPredictionsBlotterGrouping';
import { usePositionsPredictionsColumns } from './usePositionsPredictionsColumns';
import { toPredictionsServerFilter, usePositionsPredictionsFilter } from './usePositionsPredictionsFilter';

export interface PositionsPredictionsBlotterParams {
  blotterID: string;
  initialIsOpen?: boolean;
  tab: PredictionsPositionsTabProps;
  onUpdateTab: (tab: PredictionsPositionsTabProps) => void;
  onCloneTab: ClonePositionsTabFunc;
}

const predictionsAggSpecs = [{ valuePath: 'Equivalent.UnrealizedPnL', currencyPath: 'Equivalent.Currency' }] satisfies {
  valuePath: Leaves<Position>;
  currencyPath: Leaves<Position>;
}[];

export function PositionsPredictionsBlotter({
  blotterID,
  tab,
  onUpdateTab,
  initialIsOpen,
  onCloneTab,
}: PositionsPredictionsBlotterParams) {
  const mixpanel = useMixpanel();
  const { enableLegacyUpdateBalances } = useFeatureFlag();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { eventsByID, eventSeriesByID } = usePredictionsContext();

  const defaultBlotterColumns = tab.defaultColumns ?? defaultPredictionsBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultPredictionsBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;

  const defaultColumns = usePositionsPredictionsColumns(defaultBlotterColumns);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
    extraGridState: {
      rowGroupExpansion: {
        expansionState: 'all-expanded',
        expansionExceptions: [],
      },
    },
  });
  const filterResults = usePositionsPredictionsFilter({
    persistedBlotterTable,
    defaultFilter,
  });

  const { filterBuilderProps } = filterResults;

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
  });

  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const positionsMenu = usePositionsMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
    tabType: 'Predictions',
    showOpenTransactionsMenuItem: true,
  });

  const columnsWithMenu = useMemo(
    () => [...defaultColumns, ...positionsMenu.columns],
    [defaultColumns, positionsMenu.columns]
  );

  const aggPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getPositionsBlotterRowKey,
    aggSpecs: predictionsAggSpecs,
  });

  const filterFunc = useCallback(
    (row: Position) => {
      const metadata = resolvePredictionSymbolMetadata(
        getPositionsBlotterRowAsset(row),
        securitiesBySymbol,
        eventsByID,
        eventSeriesByID
      );
      return !isPredictionPositionExcludedByFilters(metadata, filterResults.filter.Series, filterResults.filter.Events);
    },
    [securitiesBySymbol, eventsByID, eventSeriesByID, filterResults.filter.Series, filterResults.filter.Events]
  );
  const filterPipe = useWSFilterPipe({ getUniqueKey: getPositionsBlotterRowKey, filterFunc });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        title: 'Grouping',
        editable: false,
        width: GROUP_COL_WIDTH,
        hide: false,
        sortable: true,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const baseRequest = usePositionsBaseRequest({ blotterID, showZeroBalances });

  const request = useMemo(
    () => ({
      ...baseRequest,
      ...removeEmptyFilters(toPredictionsServerFilter(filterResults.filter)),
    }),
    [baseRequest, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    pipe: filterPipe,
    pinnedRowDataPipe: aggPipe,
    rowID: 'rowID',
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    getExtraMainMenuItems: positionsMenu.getExtraMainMenuItems,
    contextMenuOptions: positionsMenu.contextMenuOptions,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      autoGroupColumnDef,
      groupDisplayType: 'singleColumn',
    },
    exportOptions: {
      name: 'PositionsPredictions',
      tabLabel: tab.label,
    },
  });

  const { expandAllGroups, collapseAllGroups, setRowGroupColumns } = blotterTable;

  const { onGroupingStateUpdated, groupingToggleButtons } = usePositionsPredictionsBlotterGrouping({
    setRowGroupColumns,
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const showAdjustMarketAccountPositionButton = useShowAdjustMarketAccountPositionButton();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={groupingToggleButtons}
        portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}
        prefix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} />
          </>
        }
        suffix={
          <>
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <Toggle
                tooltip={CONTROL_TOOLTIPS.showZeroBalances}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={showZeroBalances}
                onChange={updateShowZeroBalances}
                label="Show Zero Balances"
              />
              <Divider orientation="horizontal" />
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
              {showAdjustMarketAccountPositionButton && (
                <Button
                  startIcon={IconName.Pencil}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  data-testid="update-balances-button"
                  onClick={() => {
                    positionsMenu.openUpdateBalancesDialog();
                  }}
                >
                  {enableLegacyUpdateBalances ? 'Update Balances' : 'Adjust Market Account Position'}
                </Button>
              )}
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {positionsMenu.dialogs}
    </>
  );
}
